import { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, PencilIcon } from 'lucide-react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  GEEN_RECHTEN,
  kanInloggen,
  mag,
  magAdresZetten,
  magVinkjeZetten,
  RECHTENSOORTEN,
  RECHTEN_LABEL,
  rechtenTekst,
  SENIORITEITEN,
  zetVinkje,
  type OpdrachtgeverRij,
  type PersoonRij,
  type Rechten,
  type Rechtensoort,
  type Seniority,
} from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { OpdrachtenDialog } from '@/components/OpdrachtenDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { RechtenBadge } from './RechtenBadge';
import { stilteTekst } from '../begeleiding/begeleiding';
import {
  useArchiveer,
  useHistorie,
  useMaakOpdracht,
  useNodigUit,
  usePersonen,
  useVerwijderDefinitief,
  useVoegPersoonToe,
  useWijzigPersoon,
  useWijzigRechten,
  useZetActief,
  useZetTerug,
} from './personen.api';

/**
 * Eén persoon: alles wat de app over hem weet, en de plek waar je het wijzigt.
 *
 * Dit was een dialoog. Op een telefoon valt zo'n venster half buiten beeld zodra
 * er meer dan drie velden in staan, en het kon toch al niet alles tonen wat je
 * van iemand wilt weten. Als eigen pagina stapelt het vanzelf en past het op
 * allebei de maten.
 *
 * Aanmaken gebruikt dezelfde pagina, zonder gegevens: `/personen/nieuw`. Eén
 * formulier om te onderhouden in plaats van twee die uit de pas lopen.
 *
 * De indeling volgt wat je met de gegevens kunt: bovenaan wat je zelf invult,
 * daaronder wat eruit voortkomt (begeleiding, gesprekken, historie).
 *
 * **De pagina opent bij lezen.** Hier stond dat een bewerkstand niet nodig was
 * omdat die twee soorten al uit elkaar stonden. Dat hield vol zolang `Gegevens`
 * de enige kaart met knoppen was; `Begeleiding` heeft er sindsdien een keuzelijst
 * en `Koppelen`/`Ontkoppelen` bij gekregen, en daarmee was de halve pagina een
 * formulier voor iets wat je meestal alleen komt nalezen. De scheiding is er nog,
 * maar hij loopt nu tussen lezen en wijzigen.
 *
 * Eén knop voor twee kaarten, want één stand voor de hele pagina is er één om te
 * onthouden in plaats van twee die los van elkaar aan kunnen staan. `Acties`
 * blijft erbuiten: uitzetten en archiveren zijn handelingen en geen velden, en
 * ze achter de bewerkstand zetten maakt ze een klik verder zonder dat er iets
 * veiliger wordt.
 */
const LEEG = '__leeg__';

/**
 * `nieuw` komt van de route en niet uit het pad.
 *
 * Dit las `id === 'nieuw'`, en dat kon nooit kloppen: `/personen/nieuw` is een
 * vast pad zonder parameter, dus `id` was daar leeg. Aanmaken viel daardoor in
 * de tak "deze persoon bestaat niet (meer)" en het scherm was onbereikbaar.
 * De routetabel weet welk van de twee het is; die geeft het nu door.
 */
export function PersoonPagina({ nieuw = false }: { nieuw?: boolean }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = usePersonen();
  const { user } = useAuth();

  const persoon = nieuw ? null : data?.personen.find((p) => p.id === id);

  if (isLoading && !nieuw) {
    return <Skeleton className="h-96 w-full max-w-3xl" />;
  }
  // Dit scherm is bewerken. Een rij die je niet mag wijzigen hoort er dus niet
  // te openen, ook niet als je het pad zelf intikt; de server weigert de
  // historie sowieso. Terug naar de lijst, net als bij een recht dat je mist.
  if (persoon && !persoon.magWijzigen) return <Navigate replace to="/personen" />;
  if (!nieuw && !persoon) {
    return (
      <div className="flex flex-col gap-4">
        <Terug />
        <p className="text-muted-foreground">Deze persoon bestaat niet (meer).</p>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-4">
      <Terug />

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-semibold">
          {nieuw ? 'Nieuwe persoon' : persoon?.naam}
        </h1>
        {persoon && <RechtenBadge rechten={persoon.rechten} />}
        {persoon?.gearchiveerdOp && <Badge variant="outline">gearchiveerd</Badge>}
        {persoon && !persoon.actief && !persoon.gearchiveerdOp && (
          <Badge variant="outline">uitgezet</Badge>
        )}
      </div>

      <Gegevens
        onAangemaakt={(nieuwId) => navigate(`/personen/${nieuwId}`, { replace: true })}
        opdrachtgevers={data?.opdrachtgevers ?? []}
        persoon={persoon ?? null}
      />

      {persoon && (
        <>
          <Begeleiding
            magOpenen={(pid) => data?.personen.find((p) => p.id === pid)?.magWijzigen ?? false}
            persoon={persoon}
          />
          <Gesprekken persoon={persoon} />
          <Historie persoon={persoon} />
          {/* Uitzetten, archiveren en verwijderen gaan over toegang, en dat
              blijft bij de Admin. Ook op je eigen rij. */}
          {mag(user?.rechten, 'persoon:manage') && <Acties persoon={persoon} />}
        </>
      )}
    </div>
  );
}

function Terug() {
  return (
    <Button asChild className="self-start" size="sm" variant="ghost">
      <Link to="/personen">
        <ArrowLeftIcon data-icon="inline-start" />
        Personen
      </Link>
    </Button>
  );
}

/**
 * Wat je zelf invult.
 *
 * Lezen is de enige stand. Achter elk gegeven dat te wijzigen is staat een
 * potlood, en dat opent een venster voor dát ene gegeven. Eén knop die de hele
 * kaart openzette gaf twee problemen: alles stond open, ook wat je niet wilde
 * aanraken, en de opdrachtenlijst duwde de opslaanknop van het scherm.
 *
 * Aanmaken is de uitzondering en blijft één formulier: er valt dan nog niets te
 * lezen en er is geen persoon om per veld op te slaan.
 */
function Gegevens({
  persoon,
  opdrachtgevers,
  onAangemaakt,
}: {
  persoon: PersoonRij | null;
  opdrachtgevers: OpdrachtgeverRij[];
  onAangemaakt: (id: string) => void;
}) {
  if (!persoon)
    return <NieuwePersoon onAangemaakt={onAangemaakt} opdrachtgevers={opdrachtgevers} />;
  return <GegevensKaart opdrachtgevers={opdrachtgevers} persoon={persoon} />;
}

/** Welk venster openstaat. Eén tegelijk, dus één waarde en geen vlag per veld. */
type Veld = 'naam' | 'rechten' | 'niveau' | 'opdrachten' | 'email';

function GegevensKaart({
  persoon,
  opdrachtgevers,
}: {
  persoon: PersoonRij;
  opdrachtgevers: OpdrachtgeverRij[];
}) {
  const wijzigPersoon = useWijzigPersoon();
  const wijzigRechten = useWijzigRechten();
  const maakOpdracht = useMaakOpdracht();
  const { user } = useAuth();
  // Vinkjes en het adres gaan over toegang; die blijven bij de Admin, ook
  // op je eigen rij. Naam, niveau en opdracht zijn gewone gegevens.
  const magBeheren = mag(user?.rechten, 'persoon:manage');
  const magAdres = magAdresZetten(
    user?.rechten,
    { id: persoon.id, email: persoon.email, buddy: persoon.buddy },
    user?.id ?? '',
  );

  const [open, setOpen] = useState<Veld | null>(null);
  const [fout, setFout] = useState('');
  const [bevestiging, setBevestiging] = useState<{ melding: string; rechten: Rechten } | null>(
    null,
  );
  const [adresBevestiging, setAdresBevestiging] = useState<{ email: string | null } | null>(null);

  const sluit = () => {
    setOpen(null);
    setFout('');
  };

  /** Elk venster loopt hierlangs: sluiten bij succes, de melding tonen bij een
   *  fout, en het venster open laten staan zodat de invoer niet weg is. */
  async function bewaar(actie: () => Promise<unknown>) {
    setFout('');
    try {
      await actie();
      sluit();
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function bewaarRechten(rechten: Rechten, bevestigd = false) {
    setFout('');
    try {
      await wijzigRechten.mutateAsync({ id: persoon.id, rechten, bevestigd });
      sluit();
    } catch (err) {
      const melding = err instanceof ApiError ? err.message : String(err);
      // Een 409 op het afnemen van rechten is geen fout maar een vraag: er staan
      // gesprekken open die daarna voor niemand meer te openen zijn.
      if (err instanceof ApiError && err.status === 409 && !bevestigd) {
        setBevestiging({ melding, rechten });
      } else setFout(melding);
    }
  }

  /**
   * Het adres is de inlognaam, dus bij iemand die al inlogt verandert er meer
   * dan een postadres. Vandaar de bevestiging, en alleen dan: bij een leeg veld
   * of bij iemand zonder account valt er niets te waarschuwen.
   */
  async function bewaarAdres(email: string | null, bevestigd = false) {
    const raaktInlog =
      persoon.toegang === 'ja' && Boolean(persoon.email) && email !== persoon.email;
    if (raaktInlog && !bevestigd) {
      setAdresBevestiging({ email });
      return;
    }
    await bewaar(() => wijzigPersoon.mutateAsync({ id: persoon.id, email }));
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Gegevens</CardTitle>
        </CardHeader>
        <CardContent>
          <ItemGroup className="gap-3">
            <Regel label="Naam" onBewerk={() => setOpen('naam')} waarde={persoon.naam} />
            <Regel
              label="Rechten"
              onBewerk={magBeheren ? () => setOpen('rechten') : undefined}
              waarde={rechtenTekst(persoon.rechten)}
            />
            <Regel label="Niveau" onBewerk={() => setOpen('niveau')} waarde={persoon.niveau} />
            {/* Een opdrachtnaam alleen zegt te weinig: "Onderhoud Noord" krijgt
                pas betekenis met de klant ervoor. Hier staan ze allemaal onder
                elkaar; verderop in de app staat er één met een teller, want daar
                is de ruimte van een regel. */}
            <Regel
              label={persoon.opdrachten.length > 1 ? 'Opdrachten' : 'Opdracht'}
              onBewerk={() => setOpen('opdrachten')}
              waarde={persoon.opdrachten.map((o) => `${o.opdrachtgever} · ${o.naam}`)}
            />
            {/* Altijd zichtbaar, ook bij wie de app niet in komt: het adres is
                waar de app post naartoe stuurt, en dat heeft iedereen nodig.
                Het potlood volgt `magAdresZetten`: leeg vullen mag wie de rij
                mag wijzigen, een gevuld adres wijzigen alleen een Admin. */}
            <Regel
              label="E-mailadres"
              onBewerk={magAdres ? () => setOpen('email') : undefined}
              waarde={persoon.email}
            />
          </ItemGroup>
        </CardContent>
      </Card>

      <NaamDialog
        bezig={wijzigPersoon.isPending}
        fout={fout}
        naam={persoon.naam}
        onBewaar={(naam) => void bewaar(() => wijzigPersoon.mutateAsync({ id: persoon.id, naam }))}
        onSluit={sluit}
        open={open === 'naam'}
      />

      <RechtenDialog
        bezig={wijzigRechten.isPending}
        fout={fout}
        isZelf={persoon.isZelf}
        mijnRechten={user?.rechten}
        onBewaar={(r) => void bewaarRechten(r)}
        onSluit={sluit}
        open={open === 'rechten'}
        rechten={persoon.rechten}
      />

      <NiveauVeldDialog
        bezig={wijzigPersoon.isPending}
        fout={fout}
        niveau={persoon.niveau}
        onBewaar={(niveau) =>
          void bewaar(() => wijzigPersoon.mutateAsync({ id: persoon.id, niveau }))
        }
        onSluit={sluit}
        open={open === 'niveau'}
      />

      <OpdrachtenDialog
        bezig={wijzigPersoon.isPending}
        naam={persoon.naam}
        onBewaar={(opdrachtIds) =>
          void bewaar(() => wijzigPersoon.mutateAsync({ id: persoon.id, opdrachtIds }))
        }
        onFout={setFout}
        onMaakOpdracht={(invoer) => maakOpdracht.mutateAsync(invoer)}
        onSluit={sluit}
        opdrachten={persoon.opdrachten}
        opdrachtgevers={opdrachtgevers}
        open={open === 'opdrachten'}
      />

      <AdresDialog
        bezig={wijzigPersoon.isPending}
        email={persoon.email}
        fout={fout}
        onBewaar={(email) => void bewaarAdres(email)}
        onSluit={sluit}
        open={open === 'email'}
      />

      <AlertDialog
        onOpenChange={(o) => !o && setAdresBevestiging(null)}
        open={Boolean(adresBevestiging)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ook de inlognaam</AlertDialogTitle>
            <AlertDialogDescription>
              {persoon.naam} logt in met {persoon.email}. Na deze wijziging logt hij in met het
              nieuwe adres, en gaat post daar ook naartoe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const gekozen = adresBevestiging;
                setAdresBevestiging(null);
                if (gekozen) void bewaarAdres(gekozen.email, true);
              }}
            >
              Ja, doorvoeren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog onOpenChange={(o) => !o && setBevestiging(null)} open={Boolean(bevestiging)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Toch doorvoeren?</AlertDialogTitle>
            <AlertDialogDescription>{bevestiging?.melding}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const gekozen = bevestiging?.rechten;
                setBevestiging(null);
                if (gekozen) void bewaarRechten(gekozen, true);
              }}
            >
              Ja, doorvoeren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Het omhulsel van elk veldvenster: dezelfde titel, dezelfde twee knoppen, en de
 * foutmelding op dezelfde plek. Alleen de inhoud verschilt, en dat is precies
 * het veld waar het venster over gaat.
 */
function VeldDialog({
  bezig,
  children,
  fout,
  kanOpslaan,
  onBewaar,
  onSluit,
  open,
  titel,
}: {
  bezig: boolean;
  children: React.ReactNode;
  fout: string;
  kanOpslaan: boolean;
  onBewaar: () => void;
  onSluit: () => void;
  open: boolean;
  titel: string;
}) {
  return (
    <Dialog onOpenChange={(o) => !o && onSluit()} open={open}>
      <DialogContent className="max-h-[75vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titel}</DialogTitle>
        </DialogHeader>

        {children}

        {fout && (
          <Alert variant="destructive">
            <AlertTitle>{fout}</AlertTitle>
          </Alert>
        )}

        <DialogFooter>
          <Button disabled={!kanOpslaan || bezig} onClick={onBewaar}>
            {bezig && <Spinner data-icon="inline-start" />}
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NaamDialog({
  bezig,
  fout,
  naam,
  onBewaar,
  onSluit,
  open,
}: {
  bezig: boolean;
  fout: string;
  naam: string;
  onBewaar: (naam: string) => void;
  onSluit: () => void;
  open: boolean;
}) {
  const [waarde, setWaarde] = useState(naam);
  useEffect(() => {
    if (open) setWaarde(naam);
  }, [open, naam]);

  return (
    <VeldDialog
      bezig={bezig}
      fout={fout}
      kanOpslaan={waarde.trim().length >= 2 && waarde !== naam}
      onBewaar={() => onBewaar(waarde.trim())}
      onSluit={onSluit}
      open={open}
      titel="Naam"
    >
      <Field>
        <FieldLabel htmlFor="veld-naam">Naam</FieldLabel>
        <Input
          autoComplete="off"
          autoFocus
          id="veld-naam"
          onChange={(e) => setWaarde(e.target.value)}
          onKeyDown={(e) =>
            e.key === 'Enter' && waarde.trim().length >= 2 && onBewaar(waarde.trim())
          }
          value={waarde}
        />
      </Field>
    </VeldDialog>
  );
}

/**
 * Losse vinkjes, want buddy, Core Team en Admin staan los van elkaar. Dit
 * was een keuze uit drie, en daarin bestond "beide" niet.
 *
 * Wat er vaststaat rekent het venster zelf uit en niet de aanroeper: dat hangt
 * af van de stand van de vinkjes op dit moment, en die verandert bij elke klik.
 * Zie `vasteVinkjes`.
 */
function RechtenDialog({
  bezig,
  fout,
  isZelf,
  mijnRechten,
  onBewaar,
  onSluit,
  open,
  rechten,
}: {
  bezig: boolean;
  fout: string;
  isZelf: boolean;
  mijnRechten: Rechten | undefined;
  onBewaar: (rechten: Rechten) => void;
  onSluit: () => void;
  open: boolean;
  rechten: Rechten;
}) {
  const [waarde, setWaarde] = useState<Rechten>(rechten);
  useEffect(() => {
    if (open) setWaarde(rechten);
  }, [open, rechten]);

  return (
    <VeldDialog
      bezig={bezig}
      fout={fout}
      kanOpslaan={RECHTENSOORTEN.some((soort) => waarde[soort] !== rechten[soort])}
      onBewaar={() => onBewaar(waarde)}
      onSluit={onSluit}
      open={open}
      titel="Rechten"
    >
      <Vinkjes
        idPrefix="veld-rechten"
        onWissel={setWaarde}
        vast={vasteVinkjes(mijnRechten, waarde, isZelf)}
        waarde={waarde}
      />
    </VeldDialog>
  );
}

/**
 * Welke vinkjes je ziet maar niet kunt zetten.
 *
 * Drie redenen. Het Core Team-vinkje en het Admin-vinkje zijn van de Admin: wie ze zet bepaalt wie er bij het beheer van de app komt. Op je
 * eigen rij is het Admin-vinkje van iemand anders, want het is de enige klik
 * waarmee je jezelf buiten dit scherm zet; je buddyvinkje mag je wél zelf
 * zetten, anders kan een Admin er nooit een worden.
 *
 * De derde is de klik en niet het vinkje. View Only aanzetten zet de andere drie
 * uit, dus bij iemand die Core Team of Admin is, is dat voor het Core Team een verkapte manier om dat vinkje af te nemen. Dan staat View Only
 * ook vast, en niet omdat het vinkje niet van hem is maar omdat de klik meer
 * doet dan hij mag.
 */
function vasteVinkjes(
  rechten: Rechten | undefined,
  waarde: Rechten,
  isZelf: boolean,
): Rechtensoort[] {
  return RECHTENSOORTEN.filter((soort) => {
    if (!magVinkjeZetten(rechten, soort)) return true;
    if (isZelf && soort === 'admin') return true;
    const na = zetVinkje(waarde, soort, !waarde[soort]);
    return RECHTENSOORTEN.some((s) => na[s] !== waarde[s] && !magVinkjeZetten(rechten, s));
  });
}

/**
 * De vinkjes zelf, gedeeld door het venster en het aanmaakformulier.
 *
 * Twee keer dezelfde regels uitschrijven zou de kans openen dat er ergens een
 * vinkje bij komt dat maar op één van de twee plekken staat.
 *
 * `zetVinkje` en geen losse toewijzing: View Only sluit de andere drie uit, en die
 * regel hoort bij het model en niet bij dit scherm.
 */
function Vinkjes({
  idPrefix,
  onWissel,
  vast = [],
  waarde,
}: {
  idPrefix: string;
  onWissel: (rechten: Rechten) => void;
  /** Vinkjes die te zien zijn maar niet te zetten. */
  vast?: readonly Rechtensoort[];
  waarde: Rechten;
}) {
  return (
    <div className="flex flex-col gap-1">
      {RECHTENSOORTEN.map((soort) => {
        const staatVast = vast.includes(soort);
        return (
          <label
            className={
              staatVast
                ? 'flex items-center gap-2 rounded-sm px-2 py-1.5 text-muted-foreground'
                : 'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-muted/50'
            }
            htmlFor={`${idPrefix}-${soort}`}
            key={soort}
          >
            <Checkbox
              checked={waarde[soort]}
              disabled={staatVast}
              id={`${idPrefix}-${soort}`}
              onCheckedChange={(aan) => onWissel(zetVinkje(waarde, soort, aan === true))}
            />
            <span>{RECHTEN_LABEL[soort]}</span>
          </label>
        );
      })}
    </div>
  );
}

/** `NiveauVeldDialog` en niet `NiveauDialog`: die naam staat al op het venster
 *  in het gesprek, dat naast het niveau ook de bevroren stand van dat gesprek
 *  zet. Dit hier raakt alleen de administratie. */
function NiveauVeldDialog({
  bezig,
  fout,
  niveau,
  onBewaar,
  onSluit,
  open,
}: {
  bezig: boolean;
  fout: string;
  niveau: Seniority | null;
  onBewaar: (niveau: Seniority | null) => void;
  onSluit: () => void;
  open: boolean;
}) {
  const [waarde, setWaarde] = useState<Seniority | null>(niveau);
  useEffect(() => {
    if (open) setWaarde(niveau);
  }, [open, niveau]);

  return (
    <VeldDialog
      bezig={bezig}
      fout={fout}
      kanOpslaan={waarde !== niveau}
      onBewaar={() => onBewaar(waarde)}
      onSluit={onSluit}
      open={open}
      titel="Niveau"
    >
      <Field>
        <FieldLabel htmlFor="veld-niveau">Niveau</FieldLabel>
        <Select
          onValueChange={(w) => setWaarde(w === LEEG ? null : (w as Seniority))}
          value={waarde ?? LEEG}
        >
          <SelectTrigger className="w-full" id="veld-niveau">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={LEEG}>Niet ingevuld</SelectItem>
            {SENIORITEITEN.map((n) => (
              <SelectItem key={n} value={n}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </VeldDialog>
  );
}

/** Een adres zetten is hier hetzelfde als uitnodigen: zonder adres is er geen
 *  weg naar binnen, en met adres gaat de uitnodiging eruit. */
/**
 * Het adres, en niets anders. Dit venster nodigde eerder uit; dat is nu een
 * eigen knop, want uitnodigen is een handeling en een adres is een gegeven.
 *
 * Leeg opslaan mag: dat haalt het adres weg. Wie dat mag staat in
 * `magAdresZetten`, en het potlood is er dan niet.
 */
function AdresDialog({
  bezig,
  email,
  fout,
  onBewaar,
  onSluit,
  open,
}: {
  bezig: boolean;
  email: string | null;
  fout: string;
  onBewaar: (email: string | null) => void;
  onSluit: () => void;
  open: boolean;
}) {
  const [waarde, setWaarde] = useState(email ?? '');
  useEffect(() => {
    if (open) setWaarde(email ?? '');
  }, [open, email]);

  const schoon = waarde.trim();
  return (
    <VeldDialog
      bezig={bezig}
      fout={fout}
      kanOpslaan={schoon !== (email ?? '') && (schoon === '' || schoon.includes('@'))}
      onBewaar={() => onBewaar(schoon === '' ? null : schoon)}
      onSluit={onSluit}
      open={open}
      titel="E-mailadres"
    >
      <Field>
        <FieldLabel htmlFor="veld-email">E-mailadres</FieldLabel>
        <Input
          autoComplete="off"
          autoFocus
          id="veld-email"
          onChange={(e) => setWaarde(e.target.value)}
          type="email"
          value={waarde}
        />
      </Field>
    </VeldDialog>
  );
}

/**
 * Aanmaken: één formulier met alle velden en één knop.
 *
 * Bewust niet dezelfde vensters als hierboven. Die horen bij een persoon die er
 * al is; hier bestaat er nog niets om per veld op te slaan, en zes vensters
 * achter elkaar openen om iemand toe te voegen is geen formulier maar een
 * hindernisbaan.
 */
function NieuwePersoon({
  opdrachtgevers,
  onAangemaakt,
}: {
  opdrachtgevers: OpdrachtgeverRij[];
  onAangemaakt: (id: string) => void;
}) {
  const voegToe = useVoegPersoonToe();
  const maakOpdracht = useMaakOpdracht();
  const { user } = useAuth();

  const [naam, setNaam] = useState('');
  const [rechten, setRechten] = useState<Rechten>(GEEN_RECHTEN);
  const [niveau, setNiveau] = useState<Seniority | null>(null);
  const [opdrachtIds, setOpdrachtIds] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [fout, setFout] = useState('');
  const [opdrachtenOpen, setOpdrachtenOpen] = useState(false);

  const gekozenOpdrachten = opdrachtgevers.flatMap((g) =>
    g.opdrachten
      .filter((o) => opdrachtIds.includes(o.id))
      .map((o) => ({ id: o.id, naam: o.naam, opdrachtgeverId: g.id, opdrachtgever: g.naam })),
  );

  async function toevoegen() {
    setFout('');
    try {
      const uitkomst = (await voegToe.mutateAsync({
        naam,
        // Altijd meesturen: het adres hoort bij de persoon en niet bij zijn
        // vinkjes. Wie kan inloggen krijgt er meteen een uitnodiging bij.
        email: email.trim() || null,
        rechten,
        niveau,
        opdrachtIds,
      })) as { id: string };
      onAangemaakt(uitkomst.id);
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gegevens</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="persoon-naam">Naam</FieldLabel>
            <Input
              autoComplete="off"
              id="persoon-naam"
              onChange={(e) => setNaam(e.target.value)}
              value={naam}
            />
          </Field>

          <Field>
            <FieldLabel>Rechten</FieldLabel>
            <Vinkjes
              idPrefix="rechten"
              onWissel={setRechten}
              vast={vasteVinkjes(user?.rechten, rechten, false)}
              waarde={rechten}
            />
          </Field>

          {/* Iedereen kan begeleid worden, dus iedereen kan een niveau hebben. */}
          <Field>
            <FieldLabel htmlFor="persoon-niveau">Niveau</FieldLabel>
            <Select
              onValueChange={(w) => setNiveau(w === LEEG ? null : (w as Seniority))}
              value={niveau ?? LEEG}
            >
              <SelectTrigger className="w-full sm:w-64" id="persoon-niveau">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={LEEG}>Niet ingevuld</SelectItem>
                {SENIORITEITEN.map((n) => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Hetzelfde venster als op de pagina van iemand die er al is: de lijst
              is te lang om hier uitgeklapt te staan. */}
          <Field>
            <FieldLabel>Opdrachten</FieldLabel>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={gekozenOpdrachten.length === 0 ? 'text-muted-foreground' : undefined}
              >
                {gekozenOpdrachten.length === 0
                  ? 'geen opdracht'
                  : gekozenOpdrachten.map((o) => o.naam).join(', ')}
              </span>
              <Button onClick={() => setOpdrachtenOpen(true)} size="sm" variant="outline">
                <PencilIcon data-icon="inline-start" />
                Kiezen
              </Button>
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="persoon-email">E-mailadres</FieldLabel>
            <Input
              autoComplete="off"
              className="w-full sm:w-80"
              id="persoon-email"
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              value={email}
            />
          </Field>
        </FieldGroup>

        {fout && (
          <Alert className="mt-4" variant="destructive">
            <AlertTitle>{fout}</AlertTitle>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="justify-end gap-2">
        <Button
          disabled={naam.trim().length < 2 || voegToe.isPending}
          onClick={() => void toevoegen()}
        >
          {voegToe.isPending && <Spinner data-icon="inline-start" />}
          Toevoegen
        </Button>
      </CardFooter>

      <OpdrachtenDialog
        naam={naam.trim() || 'deze persoon'}
        onBewaar={(ids) => {
          setOpdrachtIds(ids);
          setOpdrachtenOpen(false);
        }}
        onFout={setFout}
        onMaakOpdracht={(invoer) => maakOpdracht.mutateAsync(invoer)}
        onSluit={() => setOpdrachtenOpen(false)}
        opdrachten={gekozenOpdrachten}
        opdrachtgevers={opdrachtgevers}
        open={opdrachtenOpen}
      />
    </Card>
  );
}

/**
 * Eén gegeven in de leesstand: een eigen kaartje met het label, de waarde en het
 * potlood dat erbij hoort.
 *
 * De kaders zijn er om het potlood te binden. Zonder kader stonden de potloden
 * op een eigen kolom rechts, ver van de waarde, en was niet te zien welk gegeven
 * je opende.
 *
 * De streep waar niets staat komt uit de personenlijst, die hem al gebruikt voor
 * een leeg niveau. Een lege regel leest anders als een storing terwijl het een
 * antwoord is.
 */
function Regel({
  label,
  onBewerk,
  waarde,
}: {
  label: string;
  /** Zonder deze prop staat er geen potlood: dan valt er hier niets te wijzigen. */
  onBewerk?: () => void;
  waarde: string | string[] | null;
}) {
  // Een lijst onder elkaar en niet achter komma's: bij twee opdrachten met een
  // lange klantnaam ervoor loopt één regel over de hele breedte door en is niet
  // meer te zien waar de ene ophoudt en de volgende begint.
  const regels = (Array.isArray(waarde) ? waarde : [waarde]).filter(Boolean) as string[];
  return (
    <Item variant="outline">
      <ItemContent className="min-w-0 gap-1">
        <ItemDescription>{label}</ItemDescription>
        {regels.length > 0 ? (
          <div className="flex flex-col gap-0.5 text-foreground">
            {regels.map((r) => (
              <span key={r}>{r}</span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </ItemContent>
      {onBewerk && (
        <ItemActions>
          <Button
            aria-label={`${label} wijzigen`}
            className="text-muted-foreground"
            onClick={onBewerk}
            size="icon-sm"
            variant="ghost"
          >
            <PencilIcon />
          </Button>
        </ItemActions>
      )}
    </Item>
  );
}

/**
 * Wie hem begeleidt, en wie hij begeleidt. Alleen lezen.
 *
 * Koppelen en ontkoppelen gebeurt op het tabblad Begeleiding, waar je het hele
 * veld overziet. Hier zou het een tweede plek zijn om hetzelfde te doen, en dan
 * ontstaan er twee gewoontes voor één handeling.
 *
 * De namen blijven aanklikbaar: doorklikken naar een collega is lezen en geen
 * wijzigen.
 */
/** `magOpenen` beslist of een naam een knop wordt of gewone tekst: de pagina
 *  achter die knop is er alleen voor wie je mag wijzigen. */
function Begeleiding({
  magOpenen,
  persoon,
}: {
  magOpenen: (persoonId: string) => boolean;
  persoon: PersoonRij;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Begeleiding</CardTitle>
      </CardHeader>
      <CardContent>
        <ItemGroup className="gap-3">
          {/* Dezelfde kaartjes als op de kaart Gegevens, alleen zonder potlood. */}
          <Item variant="outline">
            <ItemContent className="min-w-0 gap-1">
              <ItemDescription>Wordt begeleid door</ItemDescription>
              {!persoon.buddy ? (
                <span className="text-muted-foreground">niemand</span>
              ) : magOpenen(persoon.buddy.id) ? (
                <Button asChild className="self-start" size="sm" variant="secondary">
                  <Link to={`/personen/${persoon.buddy.id}`}>{persoon.buddy.naam}</Link>
                </Button>
              ) : (
                <span>{persoon.buddy.naam}</span>
              )}
            </ItemContent>
          </Item>

          <Item variant="outline">
            <ItemContent className="min-w-0 gap-1">
              <ItemDescription>Begeleidt zelf</ItemDescription>
              {persoon.begeleidt.length === 0 ? (
                <span className="text-muted-foreground">niemand</span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {persoon.begeleidt.map((m) =>
                    magOpenen(m.id) ? (
                      <Button asChild key={m.id} size="sm" variant="secondary">
                        <Link to={`/personen/${m.id}`}>{m.naam}</Link>
                      </Button>
                    ) : (
                      <span key={m.id}>{m.naam}</span>
                    ),
                  )}
                </div>
              )}
            </ItemContent>
          </Item>
        </ItemGroup>
      </CardContent>
    </Card>
  );
}

function Gesprekken({ persoon }: { persoon: PersoonRij }) {
  const nu = useMemo(() => new Date().toISOString(), []);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Gesprekken</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Cijfer label="Aangeleverd" waarde={String(persoon.gesprekken)} />
        <Cijfer label="Niet afgerond" waarde={String(persoon.onafgerond)} />
        <Cijfer label="Laatste" waarde={stilteTekst(persoon, nu)} />
      </CardContent>
    </Card>
  );
}

function Cijfer({ label, waarde }: { label: string; waarde: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-heading text-lg font-medium">{waarde}</span>
    </div>
  );
}

/** Elke koppeling die deze persoon ooit had, aan welke kant dan ook. */
function Historie({ persoon }: { persoon: PersoonRij }) {
  const historie = useHistorie(persoon.id);
  const koppelingen = historie.data?.koppelingen ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Historie</CardTitle>
      </CardHeader>
      <CardContent>
        {koppelingen.length === 0 ? (
          <p className="text-muted-foreground">Nooit aan iemand gekoppeld geweest.</p>
        ) : (
          <ItemGroup>
            {koppelingen.map((k) => (
              <Item key={k.id} size="sm" variant="muted">
                <ItemContent>
                  <ItemTitle>
                    {k.buddy.naam} begeleidde {k.medewerker.naam}
                  </ItemTitle>
                  <ItemDescription>
                    {new Date(k.startDatum).toLocaleDateString('nl-NL')} tot{' '}
                    {k.eindDatum ? new Date(k.eindDatum).toLocaleDateString('nl-NL') : 'nu'}
                  </ItemDescription>
                </ItemContent>
              </Item>
            ))}
          </ItemGroup>
        )}
      </CardContent>
    </Card>
  );
}

/** Uitzetten, archiveren, terugzetten, verwijderen. Bij elkaar en onderaan, want
 *  je komt hier zelden en het zijn de handelingen met gevolgen. */
function Acties({ persoon }: { persoon: PersoonRij }) {
  const navigate = useNavigate();
  const nodigUit = useNodigUit();
  const zetActief = useZetActief();
  const archiveer = useArchiveer();
  const zetTerug = useZetTerug();
  const verwijder = useVerwijderDefinitief();
  const [fout, setFout] = useState('');
  const [bevestig, setBevestig] = useState(false);

  async function doe(actie: () => Promise<unknown>, terug = false) {
    setFout('');
    try {
      await actie();
      if (terug) navigate('/personen');
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  if (persoon.isZelf) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Acties</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Dit ben jij. Uitzetten en archiveren kan alleen iemand anders doen, zodat de laatste
            Admin zichzelf niet buitensluit.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Acties</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {persoon.gearchiveerdOp ? (
            <>
              <Button
                disabled={zetTerug.isPending}
                onClick={() => void doe(() => zetTerug.mutateAsync(persoon.id))}
                variant="secondary"
              >
                Terugzetten
              </Button>
              <Button onClick={() => setBevestig(true)} variant="ghost">
                Definitief verwijderen
              </Button>
            </>
          ) : (
            <>
              {/* Uitnodigen is een handeling en hoort dus hier, niet achter het
                  potlood van het adres. Hij verschijnt alleen als er iets te
                  versturen is: een adres, een vinkje, en nog geen inlog. */}
              {persoon.toegang === 'nee' && persoon.email && kanInloggen(persoon.rechten) && (
                <Button
                  disabled={nodigUit.isPending}
                  onClick={() => void doe(() => nodigUit.mutateAsync(persoon.id))}
                >
                  Uitnodigen
                </Button>
              )}
              <Button
                disabled={zetActief.isPending}
                onClick={() =>
                  void doe(() => zetActief.mutateAsync({ id: persoon.id, actief: !persoon.actief }))
                }
                variant="secondary"
              >
                {persoon.actief ? 'Uitzetten' : 'Aanzetten'}
              </Button>
              <Button
                disabled={archiveer.isPending}
                onClick={() => void doe(() => archiveer.mutateAsync(persoon.id), true)}
                variant="ghost"
              >
                Archiveren
              </Button>
            </>
          )}
        </CardContent>
        {fout && (
          <CardFooter>
            <Alert variant="destructive">
              <AlertTitle>{fout}</AlertTitle>
            </Alert>
          </CardFooter>
        )}
      </Card>

      <AlertDialog onOpenChange={setBevestig} open={bevestig}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{persoon.naam} definitief verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit kan niet ongedaan worden gemaakt. De begeleidingsgeschiedenis verdwijnt mee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void doe(() => verwijder.mutateAsync(persoon.id), true)}
              variant="destructive"
            >
              Ja, definitief verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
